import React, {
    useMemo,
    forwardRef,
    useImperativeHandle,
    useEffect,
    useRef,
} from "react";

import { Modal, Button } from "react-bootstrap";

import Switch from "Common/Custom/Switch";
import FormSelect from "Common/Custom/FormSelect";
import FormPhoneInput from "Common/Custom/FormPhoneInput";
import FormDateInput from "./FormDatePicker";
import FormTextarea from "Common/Custom/FormTextarea";
import FormInput from "Common/Custom/FormInput";
import FormInputFile from "Common/Custom/FormInputFile";
import DynamicAsyncSelect from "Common/Custom/DynamicAsyncSelect";
import Spinner from "Common/Globle/Spinner";

import CountryName from "Common/json/CountryName.json";
import fields from "./json/CustomerFeilds.json";

import useFormSubmit from "Hooks/useFormSubmit";
import useFormChange from "Hooks/useFormChange";
import { useLocationCascading } from "Hooks/useLocationCascading";

import { validateForm } from "utils/validation";
import { CustomersRules } from "validation/ValidationRules";

import {
    createNewCustomer,
} from "adminApi/CreateBookingCustomer/CreateBookingCustomerApi";

import {
    handleConfirm,
    normalizeCustomerOption,
} from "../HELPERS/Helpers";

import { CreateNewGuestForm } from "./CreateNewGuestForm";


// ============================================================
// CREATE NEW CUSTOMER FORM
// ============================================================

const CreateNewCustomerForm = forwardRef(
    ({ newCustomer }, ref) => {
        /*
         * ------------------------------------------------------
         * ALL FRONTEND FIELDS
         * ------------------------------------------------------
         */

        const allFields = useMemo(
            () => [
                ...(fields.user_details || []),
                ...(fields.contact_details || []),
                ...(fields.identity_details || []),
            ],
            []
        );

        /*
         * ------------------------------------------------------
         * INITIAL VALUES
         * ------------------------------------------------------
         */

        const initialValues = useMemo(() => {
            const values = {};

            allFields.forEach((field) => {
                values[field.name] = field.name === "country" ? "India" : "";
            });

            // These are required by phone/location flow
            values.country_dial_code = "";
            values.state = "";
            values.city = "";
            values.block = "";

            return values;
        }, [allFields]);

        /*
         * ------------------------------------------------------
         * FORM CHANGE
         * ------------------------------------------------------
         */

        const {
            values,
            errors,
            handleChange,
            handleFileChange,
            setErrors,
        } = useFormChange(
            initialValues,
            CustomersRules
        );

        console.log("values", values)

        /*
         * ------------------------------------------------------
         * COUNTRY
         * ------------------------------------------------------
         */

        const selectedCountry = String(values.country || "")
            .trim()
            .toUpperCase();

        const hasCountry = Boolean(selectedCountry);
        const isIndia = selectedCountry === "INDIA";

        /*
         * ------------------------------------------------------
         * LOCATION CASCADING
         * ------------------------------------------------------
         */

        const {
            states = [],
            districts = [],
            blocks = [],
            loadingStates,
            loadingDistricts,
            loadingBlocks,
        } = useLocationCascading(values);

        /*
         * ------------------------------------------------------
         * LOCATION OPTIONS
         * ------------------------------------------------------
         */

        const stateOptions = useMemo(
            () =>
                states.map((state) => ({
                    value: state.state_code,
                    label: state.state_name,
                })),
            [states]
        );

        const districtOptions = useMemo(
            () =>
                districts.map((district) => ({
                    value: district.district_code,
                    label: district.district_name,
                })),
            [districts]
        );

        const blockOptions = useMemo(
            () =>
                blocks.map((block) => ({
                    value: block.block_code,
                    label: block.block_name,
                })),
            [blocks]
        );

        /*
         * ------------------------------------------------------
         * COUNTRY OPTIONS
         * ------------------------------------------------------
         */

        const countryOptions = useMemo(
            () =>
                CountryName.map((country) => ({
                    value: country.name,
                    label: country.name,
                })),
            []
        );

        /*
         * ------------------------------------------------------
         * SUBMIT
         * ------------------------------------------------------
         */


        const convertDateToMySQL = (date) => {
            if (!date) return null;

            const [day, month, year] = date.split("-");

            return `${year}-${month}-${day}`;
        };

        const {
            loading: loadingSubmit,
            handleSubmit,
            response,
        } = useFormSubmit(
            async (formValues) => {
                /*
                 * Do NOT modify values directly.
                 * Create a clean payload.
                 */

                const cleanValues = {
                    ...formValues,
                };

                /*
                 * Remove preview-only values
                 */

                Object.keys(cleanValues)
                    .filter((key) => key.endsWith("Preview"))
                    .forEach((key) => {
                        delete cleanValues[key];
                    });

                /*
                 * --------------------------------------------------
                 * FRONTEND LOCATION VALUES
                 * --------------------------------------------------
                 *
                 * Backend receives:
                 *
                 * country
                 * state
                 * city
                 * block
                 * address
                 *
                 */

                const formData = new FormData();

                Object.entries(cleanValues).forEach(
                    ([key, value]) => {
                        /*
                         * File values must be appended directly.
                         */

                        if (value instanceof File) {
                            formData.append(key, value);
                            return;
                        }

                        if (value instanceof FileList) {
                            if (value.length > 0) {
                                formData.append(key, value[0]);
                            }
                            return;
                        }

                        if (key == "dob") {
                            formData.append(key, convertDateToMySQL(value));
                        } else {
                            formData.append(
                                key,
                                value === null ||
                                    value === undefined
                                    ? ""
                                    : value
                            );
                        }


                    }
                );

                return await createNewCustomer({
                    data: formData,
                });
            },

            /*
             * Validation
             */

            (vals) => {
                const validationErrors = validateForm(
                    vals,
                    CustomersRules
                );

                setErrors(validationErrors);

                return validationErrors;
            },

            () => "",

            "Customer Created successfully!"
        );

        /*
         * ------------------------------------------------------
         * CUSTOMER CREATED RESPONSE
         * ------------------------------------------------------
         */

        useEffect(() => {
            if (!response?.data) {
                return;
            }

            const data = response.data;

            const {
                customer_id,
                full_name,
                mobile,
                customer_phone,
                country_dial_code,
            } = data;

            newCustomer({
                customer_id,

                full_guest_name:
                    full_name ||
                    `${data.first_name || ""} ${data.last_name || ""
                        }`.trim(),

                mobile:
                    mobile ||
                    customer_phone ||
                    data.customer_phone ||
                    "",

                country_dial_code:
                    country_dial_code ||
                    data.country_dial_code ||
                    "",
            });
        }, [response, newCustomer]);

        /*
         * ------------------------------------------------------
         * FORM SUBMIT REF
         * ------------------------------------------------------
         */

        useImperativeHandle(ref, () => ({
            submitForm: () => {
                const fakeEvent = {
                    preventDefault: () => { },
                };

                handleSubmit(fakeEvent, values);
            },
        }));

        /*
         * ------------------------------------------------------
         * FIELD RENDER
         * ------------------------------------------------------
         */

        const renderField = (field) => {
            /*
             * ==================================================
             * IDENTITY VISIBILITY
             * ==================================================
             */



            /*
             * ==================================================
             * STATE
             * ==================================================
             */

            if (field.name === "state") {
                /*
                 * No country selected
                 */

                if (!hasCountry) {
                    return (
                        <FormInput
                            key={field.name}
                            field={{
                                ...field,
                                type: "text",
                                disabled: true,
                                placeholder:
                                    "Select Country First",
                            }}
                            value=""
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );
                }

                /*
                 * India
                 */

                if (isIndia) {
                    return (
                        <FormSelect
                            key={field.name}
                            field={{
                                ...field,
                                label: "State",
                                placeholder: loadingStates
                                    ? "Loading States..."
                                    : "Select State",
                            }}
                            value={values[field.name] || ""}
                            handleChange={handleChange}
                            options={stateOptions}
                            error={errors[field.name]}
                        />
                    );
                }

                /*
                 * Foreign country
                 */

                return (
                    <FormInput
                        key={field.name}
                        field={{
                            ...field,
                            type: "text",
                            placeholder: "Enter State",
                        }}
                        value={values[field.name] || ""}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            }

            /*
             * ==================================================
             * CITY / DISTRICT
             * ==================================================
             */

            if (field.name === "city") {
                /*
                 * No country
                 */

                if (!hasCountry) {
                    return (
                        <FormInput
                            key={field.name}
                            field={{
                                ...field,
                                type: "text",
                                disabled: true,
                                placeholder:
                                    "Select Country First",
                            }}
                            value=""
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );
                }

                /*
                 * India
                 */

                if (isIndia) {
                    return (
                        <FormSelect
                            key={field.name}
                            field={{
                                ...field,
                                label: "District / City",
                                placeholder: loadingDistricts
                                    ? "Loading Districts..."
                                    : "Select District / City",
                            }}
                            value={values[field.name] || ""}
                            handleChange={handleChange}
                            options={districtOptions}
                            error={errors[field.name]}
                        />
                    );
                }

                /*
                 * Foreign
                 */

                return (
                    <FormInput
                        key={field.name}
                        field={{
                            ...field,
                            type: "text",
                            placeholder:
                                "Enter District / City",
                        }}
                        value={values[field.name] || ""}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            }

            /*
             * ==================================================
             * BLOCK / TEHSIL
             * ==================================================
             */

            if (field.name === "block") {
                /*
                 * No country
                 */

                if (!hasCountry) {
                    return (
                        <FormInput
                            key={field.name}
                            field={{
                                ...field,
                                type: "text",
                                disabled: true,
                                placeholder:
                                    "Select Country First",
                            }}
                            value=""
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );
                }

                /*
                 * India
                 */

                if (isIndia) {
                    return (
                        <FormSelect
                            key={field.name}
                            field={{
                                ...field,
                                label: "Block / Tehsil",
                                placeholder: loadingBlocks
                                    ? "Loading Blocks..."
                                    : "Select Block / Tehsil",
                            }}
                            value={values[field.name] || ""}
                            handleChange={handleChange}
                            options={blockOptions}
                            error={errors[field.name]}
                        />
                    );
                }

                /*
                 * Foreign
                 */

                return (
                    <FormInput
                        key={field.name}
                        field={{
                            ...field,
                            type: "text",
                            placeholder:
                                "Enter Block / Tehsil",
                        }}
                        value={values[field.name] || ""}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            }

            /*
             * ==================================================
             * NORMAL FIELD TYPES
             * ==================================================
             */

            switch (field.type) {
                /*
                 * FILE
                 */

                case "file":
                    return (
                        <FormInputFile
                            key={field.name}
                            field={field}
                            handleChange={handleFileChange}
                            error={errors[field.name]}
                            imagePreview=""
                        />
                    );

                /*
                 * SWITCH
                 */

                case "switch":
                    return (
                        <Switch
                            key={field.name}
                            values={values[field.name]}
                            handleChange={handleChange}
                            field={field}
                            ValueOne={
                                field.ValueOne || "active"
                            }
                            ValueTwo={
                                field.ValueTwo || "inactive"
                            }
                            LabelOne={
                                field.LabelOne || "Active"
                            }
                            LabelTwo={
                                field.LabelTwo || "Inactive"
                            }
                        />
                    );

                /*
                 * SELECT
                 */

                case "select":
                    return (
                        <FormSelect
                            key={field.name}
                            field={field}
                            value={values[field.name] || ""}
                            handleChange={handleChange}
                            options={
                                field.name === "country"
                                    ? countryOptions
                                    : field.options || []
                            }
                            error={errors[field.name]}
                        />
                    );

                /*
                 * PHONE
                 */

                case "tel":
                    return (
                        <FormPhoneInput
                            key={field.name}
                            field={field}
                            value={
                                values[field.name] || ""
                            }
                            country_code={
                                values.country_dial_code ||
                                ""
                            }
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );

                /*
                 * DATE
                 */

                case "date":
                    return (
                        <FormDateInput
                            key={field.name}
                            name={field.name}
                            label={field.label}
                            value={
                                values[field.name] || ""
                            }
                            onChange={handleChange}
                            error={errors[field.name]}
                            required={field.required}
                            placeholder={
                                field.placeholder ||
                                "DD-MM-YYYY"
                            }
                            maxDate={new Date()}
                            className={
                                field.fieldSize ||
                                "col-md-6 col-lg-4 col-12"
                            }
                        />
                    );

                /*
                 * TEXTAREA
                 */

                case "textarea":
                    return (
                        <FormTextarea
                            key={field.name}
                            field={field}
                            value={
                                values[field.name] || ""
                            }
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );

                /*
                 * DEFAULT TEXT / EMAIL
                 */

                default:
                    return (
                        <FormInput
                            key={field.name}
                            field={field}
                            value={
                                values[field.name] || ""
                            }
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );
            }
        };

        /*
         * ------------------------------------------------------
         * SECTION RENDER
         * ------------------------------------------------------
         */

        const renderSection = (
            sectionKey,
            sectionTitle
        ) => {
            const sectionFields =
                fields[sectionKey] || [];

            if (!sectionFields.length) {
                return null;
            }

            return (
                <div
                    className="card mb-3"
                    key={sectionKey}
                >
                    <div className="card-header">
                        <h6 className="card-title mb-0">
                            {sectionTitle}
                        </h6>
                    </div>

                    <div className="card-body">
                        <div className="row g-3">
                            {sectionFields.map(
                                (field, index) => (
                                    <React.Fragment
                                        key={`${sectionKey}-${field.name}-${index}`}
                                    >
                                        {renderField(field)}
                                    </React.Fragment>
                                )
                            )}
                        </div>
                    </div>
                </div>
            );
        };

        /*
         * ------------------------------------------------------
         * FORM
         * ------------------------------------------------------
         */

        return (
            <>
                {loadingSubmit && (
                    <Spinner isSpinner={true} />
                )}

                <form
                    id="create-customer-form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit(event, values);
                    }}
                >
                    <div
                        className="overflow-auto"
                        style={{
                            maxHeight: "65vh",
                            paddingRight: "4px",
                        }}
                    >
                        {renderSection(
                            "user_details",
                            "User Details"
                        )}

                        {renderSection(
                            "contact_details",
                            "Contact Details"
                        )}

                        {renderSection(
                            "identity_details",
                            "Identity Details"
                        )}
                    </div>
                </form>
            </>
        );
    }
);


// ============================================================
// CUSTOMER FORM MODAL
// ============================================================

const CustomerFormModal = ({
    show,
    setShow,
    handleExited,
    title,
    customerType,
    rootContainer,

    setSelectedCustomer,
    setSelectedCustomerId,
    selectedCustomerId,

    setNewCustomer,
    setValues,

    selectedCustomer,
    newCustomer,

    setExistingCustomers,

    setNewGuest,
    newGuest,
    setExistingGuests,

    setCustomerType,
    setTitle,
}) => {
    /*
     * ----------------------------------------------------------
     * REFS
     * ----------------------------------------------------------
     */

    const formRef = useRef(null);
    const guestFormRef = useRef(null);

    /*
     * ----------------------------------------------------------
     * EXISTING CUSTOMER SELECTED
     * ----------------------------------------------------------
     */

    const hasSelectedCustomer =
        Boolean(selectedCustomerId);

    /*
     * ----------------------------------------------------------
     * MODAL CLOSE
     * ----------------------------------------------------------
     */

    const handleClose = () => {
        setShow(false);
    };

    /*
     * ----------------------------------------------------------
     * EXISTING CUSTOMER CHANGE
     * ----------------------------------------------------------
     */

    const handleExistingCustomerChange = (
        option
    ) => {
        const normalized =
            normalizeCustomerOption(option);

        const customerId =
            normalized?.customer_id || "";

        setSelectedCustomerId(customerId);

        setSelectedCustomer(
            normalized || {}
        );

        setValues((prev) => ({
            ...prev,

            customer_id: customerId,

            full_guest_name:
                normalized?.full_guest_name ||
                "",

            mobile:
                normalized?.mobile ||
                normalized?.customer_phone ||
                "",

            country_dial_code:
                normalized?.country_dial_code ||
                "",
        }));
    };

    /*
     * ----------------------------------------------------------
     * ADD NEW CUSTOMER
     * ----------------------------------------------------------
     */

    const handleAddNewCustomer = () => {
        setSelectedCustomer(null);
        setSelectedCustomerId("");

        setTitle("Add New Customer");
        setCustomerType("new");
    };

    /*
     * ----------------------------------------------------------
     * MODAL EXIT
     * ----------------------------------------------------------
     */

    const handleModalExited = () => {
        /*
         * Keep your parent cleanup
         */

        if (handleExited) {
            handleExited();
        }
    };

    /*
     * ----------------------------------------------------------
     * MODAL SIZE
     * ----------------------------------------------------------
     */

    const modalSize =
        customerType === "new" ||
            customerType === "guest"
            ? "xl"
            : "lg";

    return (
        <Modal
            container={rootContainer}
            style={{ zIndex: 9999 }}
            show={show}
            onHide={handleClose}
            onExited={handleModalExited}
            centered
            backdrop="static"
            keyboard={false}
            size={modalSize}
        >
            {/* ==================================================
                HEADER
            ================================================== */}

            <Modal.Header closeButton>
                <Modal.Title>
                    {title}
                </Modal.Title>
            </Modal.Header>

            {/* ==================================================
                BODY
            ================================================== */}

            <Modal.Body>
                {/* ==================================================
                    EXISTING CUSTOMER
                ================================================== */}

                {customerType === "existing" && (
                    <div className="mb-3">
                        <label className="form-label">
                            Select Customer
                        </label>

                        <DynamicAsyncSelect
                            onChange={
                                handleExistingCustomerChange
                            }
                            value={
                                selectedCustomerId || null
                            }
                            placeholder="Select Customer"
                        />

                        {!hasSelectedCustomer && (
                            <small className="text-muted d-block mt-2">
                                Please select a customer
                                to continue.
                            </small>
                        )}
                    </div>
                )}

                {/* ==================================================
                    CREATE CUSTOMER
                ================================================== */}

                {customerType === "new" && (
                    <CreateNewCustomerForm
                        ref={formRef}
                        newCustomer={setNewCustomer}
                    />
                )}

                {/* ==================================================
                    CREATE GUEST
                ================================================== */}

                {customerType === "guest" && (
                    <CreateNewGuestForm
                        ref={guestFormRef}
                        onGuestCreated={setNewGuest}
                    />
                )}
            </Modal.Body>

            {/* ==================================================
                FOOTER
            ================================================== */}

            <Modal.Footer>
                {/* --------------------------------------------------
                    EXISTING CUSTOMER
                -------------------------------------------------- */}

                {customerType === "existing" && (
                    <>
                        <Button
                            variant="primary"
                            onClick={
                                handleAddNewCustomer
                            }
                        >
                            Add New
                        </Button>

                        <Button
                            variant="secondary"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="danger"
                            disabled={!hasSelectedCustomer}
                            onClick={() =>
                                handleConfirm(
                                    selectedCustomerId,
                                    selectedCustomer,
                                    customerType,
                                    setExistingCustomers,
                                    setValues,
                                    newCustomer,
                                    setShow,
                                    setSelectedCustomer
                                )
                            }
                        >
                            Yes
                        </Button>
                    </>
                )}

                {/* --------------------------------------------------
                    NEW CUSTOMER
                -------------------------------------------------- */}

                {customerType === "new" && (
                    <>
                        <Button
                            variant="secondary"
                            onClick={handleClose}
                            disabled={false}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            onClick={() =>
                                formRef.current?.submitForm()
                            }
                        >
                            Create Customer
                        </Button>
                    </>
                )}

                {/* --------------------------------------------------
                    NEW GUEST
                -------------------------------------------------- */}

                {customerType === "guest" && (
                    <>
                        <Button
                            variant="secondary"
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            onClick={() =>
                                guestFormRef.current?.submitForm()
                            }
                        >
                            Create Guest
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CustomerFormModal;